import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { sessionQuery, useSignOut } from "@/api/queries/session";
import { Avatar, type AvatarSize } from "@/components/ui/Avatar";
import { buttonClassName } from "@/components/ui/Button";
import { Menu } from "@/components/ui/Menu";
import { isSafeReturnTo } from "@/domain/identity";
import { toast } from "@/stores/toast";

/**
 * The header's account control, driven by the real session: Log in / Join when anonymous,
 * "Finish signup" for an Account without a Person, and the avatar menu once the identity is claimed.
 */
export function ViewerMenu({ size = "lg", compact = false }: { size?: AvatarSize; compact?: boolean }) {
  const { data: session, isPending } = useQuery(sessionQuery());
  const location = useLocation();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const returnTo = isSafeReturnTo(location.href) ? location.href : undefined;

  if (isPending) {
    return <Avatar size={size} />;
  }

  if (!session || session.status === "anonymous") {
    return (
      <>
        <Link
          to="/join"
          search={{ mode: "signin", returnTo }}
          className={buttonClassName({ variant: compact ? "primary" : "ghost", size: "sm" })}
        >
          Log in
        </Link>
        {!compact && (
          <Link
            to="/join"
            search={{ returnTo }}
            className={buttonClassName({ size: "sm" })}
          >
            Join
          </Link>
        )}
      </>
    );
  }

  if (!session.person) {
    return (
      <Link
        to="/welcome"
        search={{ step: "identity", returnTo }}
        className={buttonClassName({ size: "sm" })}
      >
        Finish signup
      </Link>
    );
  }

  const { person } = session;
  return (
    <Menu
      trigger={
        <button
          type="button"
          aria-label={`Account menu for @${person.handle}`}
          className="inline-flex rounded-full focus-visible:outline-2 focus-visible:outline-primary"
        >
          <Avatar
            size={size}
            imageKey={person.avatar.key}
            alt={`@${person.handle}`}
          />
        </button>
      }
      items={[
        { label: "Account settings", onSelect: () => void navigate({ to: "/settings/account" }) },
        {
          label: "Sign out",
          danger: true,
          onSelect: () =>
            signOut.mutate(undefined, {
              onSuccess: () => {
                toast.info("You're signed out");
                void navigate({ to: "/" });
              },
              onError: () => toast.error("Couldn't sign out — try again"),
            }),
        },
      ]}
    />
  );
}
